import type * as React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

type StepState = 'done' | 'current' | 'upcoming';
type StepDetailTone = 'muted' | 'success' | 'warning' | 'danger';

interface StepperStep {
  /** Stable React key. */
  id: string;
  label: React.ReactNode;
  state: StepState;
  /** Right of the label (vertical) or under it (horizontal): a date, an actor, a count. */
  detail?: React.ReactNode;
  detailTone?: StepDetailTone;
}

/** Screen-reader text for each state, so it is never conveyed by the circle alone. */
const DEFAULT_STATE_LABELS: Readonly<Record<StepState, string>> = {
  done: 'Done',
  current: 'Current step',
  upcoming: 'Not started',
};

const DETAIL_TONE: Readonly<Record<StepDetailTone, string>> = {
  muted: 'text-muted-foreground',
  success: 'text-success font-semibold',
  warning: 'text-warning font-semibold',
  danger: 'text-danger font-semibold',
};

/** 22 px circle: done = success fill + tick; current = primary ring on primary-soft; upcoming = line ring. */
function StepCircle({ state, index }: { state: StepState; index: number }) {
  return (
    <span
      aria-hidden="true"
      data-slot="step-circle"
      className={cn(
        'inline-flex size-5.5 shrink-0 items-center justify-center rounded-full text-caption leading-none font-bold',
        state === 'done' && 'bg-success text-surface',
        state === 'current' && 'border-2 border-primary bg-primary-soft text-primary',
        state === 'upcoming' && 'border-2 border-border bg-surface text-muted-foreground',
      )}
    >
      {state === 'done' ? <Check className="size-3.5" strokeWidth={3} /> : index + 1}
    </span>
  );
}

/**
 * Stepper (DESIGN_SYSTEM §4, T-093): an ordered list of steps with done / current / upcoming
 * states. Vertical rows are 36 px with the detail on the right; horizontal steps are joined by a
 * 2 px line that turns success once the step before it is done. The current step carries
 * `aria-current="step"` and every step has its state as visually hidden text.
 */
function Stepper({
  steps,
  orientation = 'vertical',
  stateLabels = DEFAULT_STATE_LABELS,
  className,
  ...props
}: Omit<React.ComponentProps<'ol'>, 'children'> & {
  steps: readonly StepperStep[];
  orientation?: 'vertical' | 'horizontal';
  stateLabels?: Readonly<Record<StepState, string>>;
}) {
  const horizontal = orientation === 'horizontal';
  return (
    <ol
      data-slot="stepper"
      data-orientation={orientation}
      className={cn('flex', horizontal ? 'items-start' : 'flex-col', className)}
      {...props}
    >
      {steps.map((step, index) => {
        const last = index === steps.length - 1;
        const muted = step.state === 'upcoming';
        const detail =
          step.detail !== undefined && step.detail !== null ? (
            <span
              className={cn(
                'text-caption',
                horizontal && 'pr-3 leading-4',
                DETAIL_TONE[step.detailTone ?? 'muted'],
              )}
            >
              {step.detail}
            </span>
          ) : null;
        const label = (
          <span
            className={cn(
              horizontal ? 'pr-3 text-label' : 'flex-1 text-body',
              step.state === 'current' && 'font-semibold',
              muted ? 'text-muted-foreground' : 'text-foreground',
            )}
          >
            <span className="sr-only">{stateLabels[step.state]}: </span>
            {step.label}
          </span>
        );
        return (
          <li
            key={step.id}
            data-state={step.state}
            aria-current={step.state === 'current' ? 'step' : undefined}
            className={
              horizontal
                ? cn('flex min-w-0 flex-col gap-1.5', last ? 'flex-none' : 'flex-1')
                : 'flex min-h-9 items-center gap-3'
            }
          >
            {horizontal ? (
              <>
                <span className="flex items-center">
                  <StepCircle state={step.state} index={index} />
                  {last ? null : (
                    <span
                      aria-hidden="true"
                      data-slot="step-connector"
                      className={cn(
                        'mx-2 h-0.5 flex-1',
                        step.state === 'done' ? 'bg-success' : 'bg-border',
                      )}
                    />
                  )}
                </span>
                {label}
                {detail}
              </>
            ) : (
              <>
                <StepCircle state={step.state} index={index} />
                {label}
                {detail}
              </>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export { Stepper, type StepperStep, type StepState };
